import { useIsMobile } from "@/hooks/use-mobile";
import {
  cn,
  formatAmount,
  getRequiredFields,
  invoiceParkStatus,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { getOwnerBomBase, routes } from "@/routes/utils";
import {
  useGetBillingCategoryByCommunity,
  useGetBillingLogsList,
  useReversePostedDocMutation,
} from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  postDocumentSearch,
  searchType,
  transactionTypeValues,
} from "../../consts";
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { MoreVertical, Undo2 } from "lucide-react";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";

const useListing = () => {
  const fromDashboard = useLocation().pathname?.includes("/dashboard");
  const base = getOwnerBomBase(useLocation().pathname);
  const {
    communityData: communityDetail,
    currentFinanceYear,
    invoiceDueDate
  } = useSelector((state: RootState) => state.currentData);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "postingDate", desc: true }]);
  const [month, setMonth] = useState<number | string>("all");
  const [monthKey] = useState<number>(Math.random() * 2);
  const [openReverseDialog, setOpenReverseDialog] = useState<boolean>(false);
  const [reverseComment, setReverseComment] = useState<string>("");
  const [reversedRow, setReversedRow] = useState<any>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const { data: billingCategory, isLoading: isLoadingBillingCategory } =
    useGetBillingCategoryByCommunity({communityId: communityDetail?.communityId});

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetBillingLogsList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "billingYear",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        ...(month != "all"
          ? [
              {
                field: "billingPeriod",
                value: month,
                operator: "EQUAL",
              },
            ]
          : []),
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "parkDocumentHeaderStatus",
          value: "POSTED",
          operator: "EQUAL",
        },
        ...(invoiceDueDate
          ? [
              {
                field: "postingDate",
                value: moment().subtract(30, "days").format("YYYY-MM-DD"),
                operator: "LESS_THAN",
              },
            ]
          : []),
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, postDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: sorting?.length
        ? sorting.map((item: any) => ({
            field: item.id,
            direction: item.desc ? "DESC" : "ASC",
          }))
        : [
            {
              field: "postingDate",
              direction: "DESC",
            },
          ],
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    refetchDoc();
  };

  const breadcrumbDataDashboard = useMemo(
    () => [
      {
        link: `/${base}/${routes.dashboard}`,
        name: `Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">Billed For the Period</span>,
      },
    ],
    []
  );

  const breadcrumbDataFinance = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.billing}`,
        name: "Billing",
      },
      {
        link: "",
        name: <span className="text-primary">Billed For the Period</span>,
      },
    ],
    []
  );

  const breadcrumbData = fromDashboard
    ? breadcrumbDataDashboard
    : breadcrumbDataFinance;

  const postedColumnsAction = [
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setReversedRow(row.row.original);
                  setOpenReverseDialog(true);
                }}
                disabled={row?.row?.original?.reversed_header}
              >
                <Undo2 className="w-4 h-4" />
                Reversal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // Define Table Columns
  const columns = [
    {
      accessorKey: "transactionType",
      header: <div>Transaction Type</div>,
      accessorFn: (row: any) =>
        (transactionTypeValues as any)[String(row?.transactionType || "")],
    },
    {
      accessorKey: "documentNumber",
      header: (
        <div>
          Posted <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row?.documentNumber || ""),
    },
    {
      accessorKey: "reversalPostDocumentHeaderId",
      header: (
        <div>
          Reversal <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.reversalPostDocumentHeaderId || ""),
      size: 50,
    },
    {
      accessorKey: "billingCategoryIds",
      header: <div>Billing Category</div>,
      accessorFn: (row: any) =>
        row.billingCategoryIds?.split(",")?.map((data: any) => {
          return (
            billingCategory?.data?.find(
              (val: any) =>
                data?.toString() == val.billingCategoryId?.toString()
            )?.categoryName || ""
          );
        }),
    },
    {
      accessorKey: "billingRunDate",
      header: <div>Billing Date</div>,
      accessorFn: (row: any) =>
        row?.billingRunDate
          ? moment(row?.billingRunDate || "").format("MM/DD/YYYY")
          : "",
    },
    {
      accessorKey: "amount",
      meta: { align: "right" },
      header: <div>Billing Amt.</div>,
      accessorFn: (row: any) =>
        formatAmount(row?.amount || "0"),
    },
    {
      accessorKey: "postingDate",
      header: <div>Posting Date</div>,
      accessorFn: (row: any) =>
        row?.postingDate
          ? moment(row?.postingDate || "").format("MM/DD/YYYY")
          : "",
    },
    // {
    //   accessorKey: "postingAmount",
    //   header: <div>Posting Amt.</div>,
    //   accessorFn: (row: any) => formatAmount(row?.postingAmount || "0"),
    // },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      enableSorting: false,
      cell: () => (
        <Badge
          variant="outline"
          className={cn(
            invoiceParkStatus["posted"]?.color,
            "rounded-full px-4 py-2"
          )}
        >
          {invoiceParkStatus["posted"]?.label}
        </Badge>
      ),
    },
    ...postedColumnsAction,
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.postDocumentHeaderId}/view`);
  };

  const onBackClickDashboard = () => {
    navigate(`/${base}/${routes.dashboard}`);
  };

  const { mutate: reversePostedDoc, isPending: isReversingPostedDoc } =
    useReversePostedDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenReverseDialog(false);
        setReverseComment("");
        setReversedRow(null);
        queryClient.invalidateQueries({
          queryKey: ["billing-logs"],
        });
        toast.success("Reversal Successfully");
      },
    });

  const onReverseBilling = () => {
    if (reverseComment?.trim()?.length < 5) {
      toast.error("Please enter a comment with at least 5 characters");
      return;
    }
    reversePostedDoc({
      data: {
        postedDocumentHeaderId: reversedRow?.postDocumentHeaderId,
        reversalComment: reverseComment,
      },
    });
  };

  const onCommentChange = (e: any) => {
    setReverseComment(e.target.value);
  };

  const onMonthChange = (val: any) => {
    setMonth(val);
    setTimeout(() => {
      refetchDoc();
    }, 100);
  };

  const onBackClick = () => {
    fromDashboard
      ? navigate(`/${base}/${routes.dashboard}`)
      : navigate(`/${routes.bom}/${routes.finance}/${routes.billing}`);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    form,
    requiredFields,
    onSubmit,
    onClear,
    communityDetail,
    onMonthChange,
    monthKey,
    month,
    isLoadingBillingCategory,
    billingCategory,
    onBackClickDashboard,
    fromDashboard,
    onBackClick,
    currentFinanceYear,
    openReverseDialog,
    setOpenReverseDialog,
    onReverseBilling,
    onCommentChange,
    isReversingPostedDoc,
    sorting,
    setSorting,
  };
};

export default useListing;
